// Export a Recharts SVG chart as a downloadable PNG at a fixed 1920×1080,
// composed onto a canvas with a title band on top and per-series legend +
// largest-gap bands underneath (color swatch + driver name + lap time). No
// external deps: the chart SVG is cloned, text styles inlined (Recharts styles
// axis text via CSS, everything else via attributes), serialized, rasterized
// scaled-to-fit into the remaining space, then the bands are drawn.

const TARGET_W = 1920;
const TARGET_H = 1080;
const PAD = 28;
const TITLE_BAND = 70;
const LEGEND_ROW = 30;
export const EXPORT_FONT = '"Inter", "Segoe UI", system-ui, sans-serif';
const FONT = EXPORT_FONT;
const ANNOT_COLOR = '#f5c842';

// Walk up from the chart element to find the effective panel background so the
// exported image matches the on-screen theme.
export function resolveBackground(el) {
  let node = el;
  while (node && node !== document.documentElement) {
    const bg = getComputedStyle(node).backgroundColor;
    if (bg && bg !== 'transparent' && !/rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*0\s*\)/.test(bg)) {
      return bg;
    }
    node = node.parentElement;
  }
  return '#0d1117';
}

export function stackSvgRects(rects) {
  if (!rects.length) return { width: 0, height: 0, placements: [] };
  const left = Math.min(...rects.map(rect => rect.left));
  const top = Math.min(...rects.map(rect => rect.top));
  const right = Math.max(...rects.map(rect => rect.left + rect.width));
  const bottom = Math.max(...rects.map(rect => rect.top + rect.height));
  return {
    width: right - left,
    height: bottom - top,
    placements: rects.map(rect => ({
      x: rect.left - left,
      y: rect.top - top,
      width: rect.width,
      height: rect.height,
    })),
  };
}

function inlineSvgTextStyles(sourceSvg, clonedSvg) {
  const srcTexts = sourceSvg.querySelectorAll('text');
  clonedSvg.querySelectorAll('text').forEach((text, index) => {
    const computed = getComputedStyle(srcTexts[index]);
    text.setAttribute('fill', computed.fill);
    text.style.fontSize = computed.fontSize;
    text.style.fontFamily = computed.fontFamily;
  });
}

function svgToImage(input) {
  const svgs = Array.isArray(input) ? input : [input];
  const layout = stackSvgRects(svgs.map(svg => svg.getBoundingClientRect()));
  const composite = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  composite.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  composite.setAttribute('width', layout.width);
  composite.setAttribute('height', layout.height);
  composite.setAttribute('viewBox', `0 0 ${layout.width} ${layout.height}`);

  svgs.forEach((svg, index) => {
    const placement = layout.placements[index];
    const clone = svg.cloneNode(true);
    clone.setAttribute('x', placement.x);
    clone.setAttribute('y', placement.y);
    clone.setAttribute('width', placement.width);
    clone.setAttribute('height', placement.height);
    // Recharts sets responsive CSS dimensions on the live SVG. Pin the clone
    // to its measured pixels so nested SVGs do not all expand to the composite
    // root and overlap one another during rasterization.
    clone.style.width = `${placement.width}px`;
    clone.style.height = `${placement.height}px`;
    inlineSvgTextStyles(svg, clone);
    composite.appendChild(clone);
  });

  const source = new XMLSerializer().serializeToString(composite);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ img, width: layout.width, height: layout.height });
    img.onerror = reject;
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(source);
  });
}

// Numbered circle badge shared by chart markers, gap text blocks and the
// head-to-head report.
export function drawBadge(ctx, x, y, num, radius = 10) {
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = ANNOT_COLOR;
  ctx.fill();
  ctx.fillStyle = '#1a1a1a';
  ctx.font = `700 ${Math.round(radius * 1.3)}px ${FONT}`;
  const prevAlign = ctx.textAlign;
  ctx.textAlign = 'center';
  ctx.fillText(String(num), x, y + 0.5);
  ctx.textAlign = prevAlign;
}

// Trigger a browser download of the canvas as a PNG.
export function downloadCanvasPng(canvas, filename) {
  return new Promise(resolve => canvas.toBlob(blob => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    resolve();
  }, 'image/png'));
}

/**
 * @param {SVGElement} svg        the primary live Recharts <svg> element
 * @param {SVGElement[]} svgs     optional ordered SVG stack to export together
 * @param {string}     title      e.g. "Summit Main · 06/19/2026"
 * @param {string}     subtitle   e.g. "Speed vs Distance"
 * @param {Array<{color:string,label:string}>} legend one entry per plotted lap
 * @param {Array<{num:number,xPx:number,text:string}>} annotations largest-gap
 *   callouts: numbered dashed markers at xPx (in SVG coordinates) on the chart
 *   + a text block below
 * @param {number}     plotTop    chart margin above the plot area (SVG px)
 * @param {number}     plotBottom chart margin below the plot area (SVG px)
 * @param {number}     plotEnd    optional annotation-line bottom in composite SVG px
 * @param {string}     watermark  small site credit, bottom-right
 * @param {string}     filename   download name, e.g. "summit_speedtrace.png"
 * @param {Element}    themeFrom  element whose background to match
 * @param {HTMLCanvasElement[]} overlays canvases drawn on top of the chart
 *   (e.g. the scatter plot paints its dots on a canvas over an SVG that only
 *   carries axes and grid); placed by their on-screen rect relative to the SVG
 * @param {{x?:string,y?:string}} axisLabels axis titles drawn into the chart's
 *   bottom / left margins, for charts whose on-screen labels are HTML rather
 *   than part of the SVG
 */
export async function exportChartPng({
  svg, svgs = null, title, subtitle, legend = [], annotations = [],
  plotTop = 22, plotBottom = 24, plotEnd = null, watermark = null, filename, themeFrom,
  overlays = [], axisLabels = null,
}) {
  const { img, width, height } = await svgToImage(svgs?.length ? svgs : svg);
  const bg = resolveBackground(themeFrom || svg);
  const svgRect = (svgs?.[0] || svg).getBoundingClientRect();

  // Measure legend layout: items flow left-to-right and wrap.
  const measure = document.createElement('canvas').getContext('2d');
  measure.font = `16px ${FONT}`;
  const swatch = 30;                     // swatch line + gap before the label
  const itemGap = 36;
  const maxW = TARGET_W - PAD * 2;
  let x = 0, rows = legend.length ? 1 : 0;
  const placed = legend.map(({ color, label }) => {
    const w = swatch + measure.measureText(label).width;
    if (x > 0 && x + w > maxW) { rows += 1; x = 0; }
    const pos = { color, label, x, row: rows - 1 };
    x += w + itemGap;
    return pos;
  });
  const legendBand = rows ? rows * LEGEND_ROW + PAD : PAD;
  const gapsBand = annotations.length ? 26 + annotations.length * LEGEND_ROW + PAD / 2 : 0;

  // The chart fills whatever the fixed-size bands leave, scaled uniformly
  // (SVG rasterizes crisply at any draw size) and centered in the leftover.
  const chartAreaH = TARGET_H - TITLE_BAND - legendBand - gapsBand;
  const f = Math.min(TARGET_W / width, chartAreaH / height);
  const drawW = width * f, drawH = height * f;
  const offX = (TARGET_W - drawW) / 2;
  const offY = TITLE_BAND + (chartAreaH - drawH) / 2;

  const canvas = document.createElement('canvas');
  canvas.width = TARGET_W;
  canvas.height = TARGET_H;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, TARGET_W, TARGET_H);

  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#e8edf2';
  ctx.font = `600 24px ${FONT}`;
  ctx.fillText(title, PAD, 28);
  if (subtitle) {
    ctx.fillStyle = '#8b98a5';
    ctx.font = `15px ${FONT}`;
    ctx.fillText(subtitle, PAD, 54);
  }
  if (watermark) {
    ctx.fillStyle = '#5a6672';
    ctx.font = `14px ${FONT}`;
    const prevAlign = ctx.textAlign;
    ctx.textAlign = 'right';
    ctx.fillText(watermark, TARGET_W - PAD, TARGET_H - 18);
    ctx.textAlign = prevAlign;
  }

  ctx.drawImage(img, offX, offY, drawW, drawH);
  overlays.forEach(canvas => {
    const r = canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    ctx.drawImage(canvas,
      offX + (r.left - svgRect.left) * f, offY + (r.top - svgRect.top) * f,
      r.width * f, r.height * f);
  });
  if (axisLabels?.x || axisLabels?.y) {
    ctx.fillStyle = '#8b98a5';
    ctx.font = `15px ${FONT}`;
    ctx.textAlign = 'center';
    if (axisLabels.x) ctx.fillText(axisLabels.x, offX + drawW / 2, offY + drawH - 10);
    if (axisLabels.y) {
      ctx.save();
      ctx.translate(offX + 14, offY + drawH / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(axisLabels.y, 0, 0);
      ctx.restore();
    }
    ctx.textAlign = 'left';
  }

  // Largest-gap markers: dashed vertical line + numbered badge on the chart.
  // Marker coordinates arrive in SVG space — map through the same scale/offset.
  annotations.forEach(({ num, xPx }) => {
    const mx = offX + xPx * f;
    const top = offY + plotTop * f;
    const bottom = offY + (plotEnd ?? (height - plotBottom)) * f;
    ctx.strokeStyle = ANNOT_COLOR;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 6]);
    ctx.globalAlpha = 0.65;
    ctx.beginPath();
    ctx.moveTo(mx, top + 14);
    ctx.lineTo(mx, bottom);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
    drawBadge(ctx, mx, top + 3, num);
  });

  ctx.font = `16px ${FONT}`;
  placed.forEach(({ color, label, x: ix, row }) => {
    const y = TITLE_BAND + chartAreaH + PAD / 2 + row * LEGEND_ROW + LEGEND_ROW / 2;
    ctx.strokeStyle = color;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(PAD + ix, y);
    ctx.lineTo(PAD + ix + 20, y);
    ctx.stroke();
    ctx.fillStyle = '#c9d4dd';
    ctx.fillText(label, PAD + ix + swatch, y + 0.5);
  });

  // "Largest gaps" text block under the legend, matching the chart badges.
  if (annotations.length) {
    let y = TITLE_BAND + chartAreaH + legendBand + 12;
    ctx.fillStyle = '#8b98a5';
    ctx.font = `600 14px ${FONT}`;
    ctx.fillText('LARGEST GAPS', PAD, y);
    y += 10;
    annotations.forEach(({ num, text }) => {
      y += LEGEND_ROW;
      drawBadge(ctx, PAD + 10, y - LEGEND_ROW / 2 + 5, num);
      ctx.fillStyle = '#c9d4dd';
      ctx.font = `16px ${FONT}`;
      ctx.fillText(text, PAD + 30, y - LEGEND_ROW / 2 + 5.5);
    });
  }

  await downloadCanvasPng(canvas, filename);
}
